import React from 'react';
import { HeartIcon, CalendarDaysIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import PlaybookSection from '../../../components/playbooks/PlaybookSection';
import PlaybookCard from '../../../components/playbooks/PlaybookCard';
import { Link } from 'react-router-dom';

const PerformanceFrameworkFinalThoughts = () => (
  <div className="space-y-8">
    <PlaybookSection title="Final Thoughts" icon={<HeartIcon className="h-7 w-7 text-primary-light" />} heading>
      <PlaybookCard>
        <h3 className="text-2xl font-semibold text-white mb-6">Building Performance Frameworks That Actually Works</h3>
        
        <div className="space-y-6">
          <p className="text-gray-300 text-lg">
            Congratulations on working through this comprehensive framework! Building effective performance practices 
            is one of the most impactful investments you can make in your organization's long-term success.
          </p>

          <div className="bg-primary/10 border border-primary/30 rounded-lg p-6">
            <h4 className="text-white font-semibold mb-4">Remember the Core Principles:</h4>
            <ul className="space-y-3">
              <li className="flex items-start">
                <span className="text-primary-light mr-3 font-bold">—</span>
                <span className="text-gray-300">Performance conversations should be as regular and continuous as possible</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary-light mr-3 font-bold">—</span>
                <span className="text-gray-300">Lightweight and user-friendly beats cumbersome and over-engineered every time</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary-light mr-3 font-bold">—</span>
                <span className="text-gray-300">Transparency is a powerful lever—lean into it wherever you can</span>
              </li>
            </ul>
          </div>

          <p className="text-gray-300">
            The framework you've built isn't just about assessment—it's about creating clarity, building trust, 
            and helping everyone in your organization understand what excellence looks like. When done well, 
            performance frameworks become invisible infrastructure that enables great work to happen.
          </p>

          <div className="bg-gray-800/50 p-6 rounded-lg border border-gray-700">
            <h4 className="text-white font-semibold mb-3">What You've Accomplished:</h4>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <h5 className="text-primary-light font-medium mb-2">Foundation</h5>
                <ul className="space-y-1 text-sm text-gray-300">
                  <li>• Clear performance philosophy</li>
                  <li>• Solid building blocks assessment</li>
                  <li>• Aligned stakeholder expectations</li>
                </ul>
              </div>
              <div>
                <h5 className="text-primary-light font-medium mb-2">Framework</h5>
                <ul className="space-y-1 text-sm text-gray-300">
                  <li>• Structured assessment approach</li>
                  <li>• Manager-ready materials</li>
                  <li>• Embedded continuous practices</li>
                </ul>
              </div>
            </div>
          </div>

          <p className="text-gray-300">
            Most importantly, remember that this is never a finished product. The best performance frameworks 
            evolve with your organization, adapting to new challenges while maintaining their core purpose: 
            helping people do their best work and helping the business achieve its goals.
          </p>

          <div className="bg-gray-900/80 border border-primary/20 rounded-lg p-6">
            <h4 className="text-white font-semibold mb-3">Keep Iterating</h4>
            <p className="text-gray-300 text-sm mb-4">
              Run regular retrospectives on your performance practices. Ask your team what's working, 
              what isn't, and how the framework can better serve both individual growth and business objectives.
            </p>
            <p className="text-gray-300 text-sm">
              The moment you stop treating performance as a practice and start treating it as just a process, 
              it loses its power to drive real impact.
            </p>
          </div>

          <div className="bg-gray-800/50 p-6 rounded-lg border border-gray-700">
            <div className="flex items-center mb-4">
              <CalendarDaysIcon className="h-6 w-6 text-primary-light mr-3" />
              <h4 className="text-white font-semibold">Your Implementation Timeline</h4>
            </div>
            
            <div className="grid md:grid-cols-3 gap-6">
              <div className="space-y-3">
                <h5 className="text-primary-light font-medium">First 30 Days</h5>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start">
                    <CheckCircleIcon className="h-4 w-4 text-green-400 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-300">Run first performance cycle</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircleIcon className="h-4 w-4 text-green-400 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-300">Gather manager feedback</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircleIcon className="h-4 w-4 text-green-400 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-300">Document lessons learned</span>
                  </li>
                </ul>
              </div>
              
              <div className="space-y-3">
                <h5 className="text-primary-light font-medium">First 90 Days</h5>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start">
                    <CheckCircleIcon className="h-4 w-4 text-green-400 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-300">Complete cycle retrospective</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircleIcon className="h-4 w-4 text-green-400 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-300">Refine process based on feedback</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircleIcon className="h-4 w-4 text-green-400 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-300">Train additional managers</span>
                  </li>
                </ul>
              </div>
              
              <div className="space-y-3">
                <h5 className="text-primary-light font-medium">First 6 Months</h5>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start">
                    <CheckCircleIcon className="h-4 w-4 text-green-400 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-300">Assess business impact</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircleIcon className="h-4 w-4 text-green-400 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-300">Plan framework evolution</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircleIcon className="h-4 w-4 text-green-400 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-300">Integrate with compensation</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-gray-800/50 p-6 rounded-lg border border-gray-700">
            <h4 className="text-white font-semibold mb-4">Maintenance Calendar</h4>
            <p className="text-gray-300 text-sm mb-4">
              Keep your framework healthy with regular check-ins:
            </p>
            
            <div className="space-y-4">
              <div className="border-l-4 border-blue-500 pl-4">
                <h5 className="text-white font-medium">Monthly</h5>
                <p className="text-gray-300 text-sm">Review manager confidence levels, address friction points, update documentation</p>
              </div>
              
              <div className="border-l-4 border-green-500 pl-4">
                <h5 className="text-white font-medium">Quarterly</h5>
                <p className="text-gray-300 text-sm">Performance cycle retrospectives, process refinements, manager skills development</p>
              </div>
              
              <div className="border-l-4 border-purple-500 pl-4">
                <h5 className="text-white font-medium">Annually</h5>
                <p className="text-gray-300 text-sm">Framework effectiveness review, competency updates, strategic alignment check</p>
              </div>
            </div>
            
            <div className="mt-4 p-4 bg-primary/10 border border-primary/30 rounded-lg">
              <p className="text-gray-300 text-sm">
                <strong className="text-white">Pro tip:</strong> Set calendar reminders for these check-ins. Framework maintenance is like code maintenance—skip it at your peril!
              </p>
            </div>
          </div>

          <div className="border-t border-gray-700 pt-6">
            <h4 className="text-white font-semibold mb-3">What's Next?</h4>
            <p className="text-gray-300 mb-4">
              As your framework matures, consider exploring other OpenOrg playbooks that complement performance management:
            </p>
            <ul className="space-y-2">
              <li className="flex items-start">
                <span className="text-primary-light mr-3 font-bold">—</span>
                <span className="text-gray-300">
                  <Link to="/playbooks/feedback-culture" className="text-primary hover:text-primary-light">
                    Building a Feedback Culture
                  </Link> - Embed continuous feedback into daily operations
                </span>
              </li>
              <li className="flex items-start">
                <span className="text-primary-light mr-3 font-bold">—</span>
                <span className="text-gray-300">
                  <Link to="/playbooks/manager-community" className="text-primary hover:text-primary-light">
                    Manager Community Building
                  </Link> - Support your managers in using these frameworks effectively
                </span>
              </li>
              <li className="flex items-start">
                <span className="text-primary-light mr-3 font-bold">—</span>
                <span className="text-gray-300">
                  <Link to="/playbooks/transparency-charter" className="text-primary hover:text-primary-light">
                    Transparency Charter
                  </Link> - Create organizational transparency that supports performance clarity
                </span>
              </li>
            </ul>
          </div>

          <div className="text-center py-8 border-t border-gray-700">
            <p className="text-lg text-gray-300 mb-4">
              Need ongoing support with your performance framework implementation?
            </p>
            <p className="text-gray-300 text-sm">
              <a href="mailto:john@openorg.fyi" className="text-primary hover:text-primary-light underline">
                Get in touch
              </a> for personalized guidance, troubleshooting, or advanced optimization strategies.
            </p>
            <p className="text-gray-400 text-xs mt-2">
              We're here to help you build performance practices that actually work for your unique context.
            </p>
          </div>
        </div>
      </PlaybookCard>
    </PlaybookSection>
  </div>
);

export default PerformanceFrameworkFinalThoughts; 